import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { registrationService, getErrorMessage } from '../../../services/apiService';

const CODE_LENGTH = 4;

/**
 * Email-ownership verification for Step 1.
 *
 * idle      → "Send verification code"
 * sent      → six-box code entry, expiry countdown, resend cooldown
 * verified  → locked, green "Verified" state (also the initial state for resumed sessions)
 *
 * The server is the source of truth for the address and every rule (expiry, attempts,
 * cooldown, hourly cap); this component only renders those rules honestly.
 */
const EmailOtpPanel = ({ sessionId, email, verified, onVerified, disabled = false }) => {
  const [phase, setPhase] = useState(verified ? 'verified' : 'idle'); // idle | sending | sent | verifying | verified
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [resendAt, setResendAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [shake, setShake] = useState(false);
  const inputRefs = useRef([]);

  // External "verified" (e.g. resumed session) always wins.
  useEffect(() => {
    if (verified) setPhase('verified');
  }, [verified]);

  // One shared 1 s clock for both countdowns while a code is live.
  useEffect(() => {
    if (phase !== 'sent' && phase !== 'verifying') return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [phase]);

  const secondsLeft = expiresAt ? Math.max(0, Math.ceil((expiresAt - now) / 1000)) : 0;
  const resendIn = resendAt ? Math.max(0, Math.ceil((resendAt - now) / 1000)) : 0;
  const expired = phase === 'sent' && expiresAt !== null && secondsLeft === 0;
  const code = digits.join('');

  const focusBox = (index) => {
    const el = inputRefs.current[Math.min(Math.max(index, 0), CODE_LENGTH - 1)];
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const resetDigits = (focus = true) => {
    setDigits(Array(CODE_LENGTH).fill(''));
    if (focus) setTimeout(() => focusBox(0), 0);
  };

  const sendCode = useCallback(async () => {
    if (!sessionId || disabled) return;
    setError(null);
    setAttemptsRemaining(null);
    setPhase('sending');
    try {
      const res = await registrationService.sendEmailOtp(sessionId);
      const data = res?.data || {};
      setMaskedEmail(data.email_masked || null);
      setExpiresAt(data.expires_at ? new Date(data.expires_at).getTime() : Date.now() + 10 * 60 * 1000);
      setResendAt(data.resend_available_at ? new Date(data.resend_available_at).getTime() : Date.now() + 30 * 1000);
      setNow(Date.now());
      setPhase('sent');
      resetDigits(true);
    } catch (e) {
      setError(getErrorMessage(e));
      // If a code is already live (e.g. cooldown hit on resend) stay in 'sent'; otherwise back to idle.
      setPhase((p) => (p === 'sending' && expiresAt ? 'sent' : 'idle'));
    }
  }, [sessionId, disabled, expiresAt]);

  const verifyCode = useCallback(
    async (candidate) => {
      if (!sessionId || candidate.length !== CODE_LENGTH) return;
      setPhase('verifying');
      setError(null);
      try {
        await registrationService.verifyEmailOtp(sessionId, candidate);
        setPhase('verified');
        onVerified?.();
      } catch (e) {
        const details = e?.response?.data?.error?.details;
        const remaining = details?.attempts_remaining?.[0];
        if (remaining !== undefined) setAttemptsRemaining(Number(remaining));
        setError(getErrorMessage(e));
        setShake(true);
        setTimeout(() => setShake(false), 400);
        setPhase('sent');
        resetDigits(true);
      }
    },
    [sessionId, onVerified]
  );

  // Auto-submit the moment the sixth digit lands.
  useEffect(() => {
    if (phase === 'sent' && code.length === CODE_LENGTH && !expired) {
      verifyCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, phase]);

  const handleChange = (index, value) => {
    const cleaned = value.replace(/\D/g, '');
    if (!cleaned) {
      setDigits((d) => {
        const next = [...d];
        next[index] = '';
        return next;
      });
      return;
    }
    // Typing or pasting several digits into one box spreads them forward.
    setDigits((d) => {
      const next = [...d];
      for (let i = 0; i < cleaned.length && index + i < CODE_LENGTH; i++) {
        next[index + i] = cleaned[i];
      }
      return next;
    });
    focusBox(index + cleaned.length);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        setDigits((d) => {
          const next = [...d];
          next[index] = '';
          return next;
        });
      } else if (index > 0) {
        setDigits((d) => {
          const next = [...d];
          next[index - 1] = '';
          return next;
        });
        focusBox(index - 1);
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusBox(index - 1);
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      focusBox(index + 1);
      e.preventDefault();
    } else if (e.key === 'Enter' && code.length === CODE_LENGTH) {
      verifyCode(code);
    }
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
    if (!text) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < Math.min(text.length, CODE_LENGTH); i++) next[i] = text[i];
    setDigits(next);
    focusBox(Math.min(text.length, CODE_LENGTH - 1));
  };

  const mmss = useMemo(() => {
    const m = Math.floor(secondsLeft / 60);
    const s = secondsLeft % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }, [secondsLeft]);

  // ---------- verified ----------
  if (phase === 'verified') {
    return (
      <div
        className="flex items-center gap-3 rounded-lg border border-success/30 bg-success/5 px-4 py-3 animate-rise-in"
        role="status"
        aria-live="polite"
      >
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-success text-success-foreground animate-otp-pop motion-reduce:animate-none">
          <Icon name="Check" size={18} strokeWidth={3} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">Email verified</p>
          <p className="truncate text-xs text-text-secondary">{email}</p>
        </div>
      </div>
    );
  }

  // ---------- idle / sending ----------
  if (phase === 'idle' || (phase === 'sending' && !expiresAt)) {
    return (
      <div className="rounded-lg border border-border bg-white px-4 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon name="MailCheck" size={18} />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground">Verify your email to continue</p>
              <p className="text-xs text-text-secondary">
                We'll send a {CODE_LENGTH}-digit code to <span className="font-medium text-foreground">{email}</span>. It expires in 10 minutes.
              </p>
            </div>
          </div>
          <Button
            variant="default"
            onClick={sendCode}
            loading={phase === 'sending'}
            disabled={disabled || !sessionId || phase === 'sending'}
            iconName="Send"
            iconPosition="right"
            className="sm:flex-shrink-0"
          >
            Send verification code
          </Button>
        </div>
        {error && (
          <p className="mt-3 flex items-center gap-2 text-sm text-destructive" role="alert">
            <Icon name="AlertCircle" size={16} />
            {error}
          </p>
        )}
      </div>
    );
  }

  // ---------- sent / verifying ----------
  const busy = phase === 'verifying' || phase === 'sending';
  return (
    <div className="rounded-lg border border-primary/30 bg-white px-4 py-4 shadow-sm animate-rise-in motion-reduce:animate-none">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">Enter the {CODE_LENGTH}-digit code</p>
          <p className="text-xs text-text-secondary" aria-live="polite">
            Sent to <span className="font-medium text-foreground">{maskedEmail || email}</span>.
            {!expired && (
              <>
                {' '}Expires in{' '}
                <span className="font-medium tabular-nums text-foreground">{mmss}</span>
              </>
            )}
          </p>
        </div>
        <div className="text-xs text-text-secondary md:text-right">
          Didn't get it? Check spam, or{' '}
          <button
            type="button"
            onClick={sendCode}
            disabled={busy || resendIn > 0}
            className="font-medium text-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:text-text-secondary disabled:no-underline"
          >
            {resendIn > 0 ? (
              <>
                resend in <span className="tabular-nums">{resendIn}s</span>
              </>
            ) : (
              'resend code'
            )}
          </button>
        </div>
      </div>

      <fieldset
        className={`mt-4 flex items-center gap-2 sm:gap-3 ${shake ? 'animate-otp-shake motion-reduce:animate-none' : ''}`}
        aria-label="Verification code"
        disabled={busy || expired}
      >
        {digits.map((d, i) => (
          <React.Fragment key={i}>
            <input
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={CODE_LENGTH}
              value={d}
              aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              onFocus={(e) => e.target.select()}
              className={[
                'h-12 w-10 sm:h-14 sm:w-12 rounded-lg border text-center text-xl sm:text-2xl font-semibold tabular-nums text-foreground',
                'transition-[border-color,box-shadow,background-color] duration-150',
                'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 focus:border-primary',
                'disabled:cursor-not-allowed disabled:opacity-60',
                error ? 'border-destructive bg-destructive/5' : d ? 'border-primary/60 bg-primary/5' : 'border-border bg-white',
              ].join(' ')}
            />
            {i === Math.floor(CODE_LENGTH / 2) - 1 && <span className="w-2 border-t-2 border-border sm:w-3" aria-hidden="true" />}
          </React.Fragment>
        ))}
        {phase === 'verifying' && (
          <span className="ml-1 flex items-center gap-2 text-sm text-text-secondary" role="status">
            <Icon name="Loader2" size={16} className="animate-spin" />
            Verifying…
          </span>
        )}
      </fieldset>

      <div className="mt-3 min-h-[1.25rem] text-sm" aria-live="assertive">
        {expired ? (
          <p className="flex items-center gap-2 text-warning" role="alert">
            <Icon name="Clock" size={16} />
            This code has expired — request a new one above.
          </p>
        ) : error ? (
          <p className="flex items-center gap-2 text-destructive" role="alert">
            <Icon name="AlertCircle" size={16} />
            <span>
              {error}
              {attemptsRemaining !== null && attemptsRemaining > 0 && (
                <span className="text-text-secondary">
                  {' '}· {attemptsRemaining} {attemptsRemaining === 1 ? 'attempt' : 'attempts'} left
                </span>
              )}
            </span>
          </p>
        ) : (
          <p className="text-text-secondary">The code is verified automatically as soon as you enter the last digit.</p>
        )}
      </div>
    </div>
  );
};

export default EmailOtpPanel;
